import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, User, Tag, ArrowLeft, Search } from 'lucide-react';
import { Input } from './ui/input';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Blog card component
function BlogCard({ post, index }) {
  const navigate = useNavigate();
  
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={() => navigate(`/blog/${post.slug}`)}
      className="group cursor-pointer"
      data-testid={`blog-card-${post.slug}`}
    >
      <div className="relative overflow-hidden rounded-md bg-surface border border-line-1 hover:border-line-3 transition-all duration-300 h-full">
        {/* Featured Image */}
        {post.featured_image && (
          <div className="relative h-48 overflow-hidden border-b border-line-1">
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
          </div>
        )}
        
        {/* Content */}
        <div className="p-6">
          {/* Category & Tags */}
          <div className="flex items-center gap-3 mb-3">
            {post.category && (
              <span className="focus-tag">
                {post.category}
              </span>
            )}
          </div>
          
          {/* Title */}
          <h3 className="font-heading text-xl font-semibold text-stark mb-3 group-hover:text-stark transition-colors duration-300 line-clamp-2 tracking-tight">
            {post.title}
          </h3>
          
          {/* Excerpt */}
          <p className="text-stark-2 text-sm leading-relaxed mb-4 line-clamp-3">
            {post.excerpt}
          </p>
          
          {/* Meta */}
          <div className="flex items-center justify-between pt-4 border-t border-line-1">
            <div className="flex items-center gap-4 text-xs text-stark-3 font-mono">
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {post.author}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(post.created_at).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            </div>
            <ArrowRight className="w-4 h-4 text-stark-3 group-hover:text-stark group-hover:translate-x-1 transition-transform duration-300" />
          </div>
        </div>
      </div>
    </motion.article>
  );
}

// Blog listing page
export function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog?status=published&limit=50`);
      setPosts(response.data);
    } catch (error) {
      logError('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/categories/list`);
      setCategories(response.data.categories || []);
    } catch (error) {
      logError('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [fetchPosts, fetchCategories]);

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-ink pt-24 pb-20" data-testid="blog-page">
      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-stark-2 hover:text-stark mb-6 transition-colors text-sm font-mono"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <p className="eyebrow section-eyebrow-line mb-6">
            Insights & Resources
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-stark mb-6 tracking-tight">
            The ServeNetics{' '}
            <span className="text-gradient">Blog</span>
          </h1>
          <p className="text-lg text-stark-2 max-w-2xl leading-relaxed">
            Explore insights on AI innovation, digital transformation, and how technology 
            can empower businesses and communities worldwide.
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col md:flex-row gap-4 mb-12"
        >
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stark-3" />
            <Input
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="blog-search"
            />
          </div>
          
          {categories.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-4 py-2 rounded-md text-sm font-mono uppercase tracking-wider transition-colors border ${
                  !selectedCategory 
                    ? 'bg-white text-black border-white' 
                    : 'bg-transparent text-stark-2 border-line-1 hover:border-line-2 hover:text-stark'
                }`}
                data-testid="category-all"
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-md text-sm font-mono uppercase tracking-wider transition-colors border ${
                    selectedCategory === cat 
                      ? 'bg-white text-black border-white' 
                      : 'bg-transparent text-stark-2 border-line-1 hover:border-line-2 hover:text-stark'
                  }`}
                  data-testid={`category-${cat}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Posts Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={`post-skeleton-${i}`} className="rounded-md bg-surface h-80 animate-pulse" />
            ))}
          </div>
        ) : filteredPosts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post, index) => (
              <BlogCard key={post.id} post={post} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-stark-2 text-lg">
              {searchQuery || selectedCategory 
                ? 'No articles found matching your criteria.' 
                : 'No blog posts yet. Check back soon!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Single blog post page
export function BlogPostPage({ slug }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const navigate = useNavigate();

  const fetchPost = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
      
      // Fetch related posts
      if (response.data.category) {
        const relatedResponse = await axios.get(`${API}/blog?category=${response.data.category}&limit=3`);
        setRelatedPosts(relatedResponse.data.filter(p => p.slug !== slug).slice(0, 2));
      }
    } catch (error) {
      logError('Error fetching blog post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => {
    if (slug) {
      fetchPost();
    }
  }, [slug, fetchPost]);

  if (loading) {
    return (
    <div className="min-h-screen bg-ink pt-24 pb-20" data-testid="blog-post-page">
      <div className="max-w-[900px] mx-auto px-6 md:px-12">
        <div className="animate-pulse">
          <div className="h-8 bg-surface rounded w-1/4 mb-4" />
          <div className="h-12 bg-surface rounded w-3/4 mb-8" />
          <div className="h-64 bg-surface rounded mb-8" />
          <div className="space-y-4">
            <div className="h-4 bg-surface rounded w-full" />
            <div className="h-4 bg-surface rounded w-5/6" />
            <div className="h-4 bg-surface rounded w-4/5" />
          </div>
        </div>
      </div>
    </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-ink pt-24 pb-20" data-testid="blog-post-page">
      <article className="max-w-[900px] mx-auto px-6 md:px-12">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-stark-2 hover:text-stark mb-8 transition-colors text-sm font-mono"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </motion.div>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8"
        >
          {post.category && (
            <span className="focus-tag mb-4 inline-block">
              {post.category}
            </span>
          )}
          
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-stark mb-6 leading-tight tracking-tight" data-testid="post-title">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-stark-3 font-mono">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(post.created_at).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {Math.ceil(post.content.split(' ').length / 200)} min read
            </span>
          </div>
        </motion.header>

        {/* Featured Image */}
        {post.featured_image && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-md overflow-hidden mb-10 border border-line-1"
          >
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-auto object-cover"
            />
          </motion.div>
        )}

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="prose prose-invert prose-lg max-w-none mb-12"
          data-testid="post-content"
        >
          <div 
            className="text-stark/85 leading-relaxed space-y-6 text-lg"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content.replace(/\n/g, '<br/>')) }}
          />
        </motion.div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-3 mb-12 pt-8 border-t border-line-1"
          >
            <Tag className="w-4 h-4 text-stark-3" />
            <div className="flex gap-2 flex-wrap">
              {post.tags.map((tag) => (
                <span 
                  key={tag} 
                  className="focus-tag"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="pt-12 border-t border-line-1"
          >
            <h3 className="font-heading text-2xl font-semibold text-stark mb-6 tracking-tight">
              Related Articles
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map((relatedPost, index) => (
                <BlogCard key={relatedPost.id} post={relatedPost} index={index} />
              ))}
            </div>
          </motion.div>
        )}
      </article>
    </div>
  );
}

export default BlogPage;
